<script lang="ts">
	import { browser } from '$app/environment';
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import { user } from '@repo/sharedlib/src/stores/auth.store';
	import constants from '@repo/sharedlib/src/utils/constants';
	import { toTitleCase } from '@repo/toolslib/src/utils/misc';
	import { Button } from '@ui-components/ui/button/index';
	import * as DropdownMenu from '@ui-components/ui/dropdown-menu/index';
	import { onMount, tick } from 'svelte';
	import { getKeyboardShortcut } from '../lib/detector';
	import HelpSearchCommand from './HelpSearchCommand.svelte';
	import MobileNav from './MobileNav.svelte';

	const appUrl = resolve('/app' as any);
	const blogUrl = resolve('/blog' as any);

	// Platform detection for download URLs
	function detectPlatform(): 'macos' | 'windows' {
		if (!browser) return 'macos'; // Default for SSR
		const userAgent = navigator.userAgent.toLowerCase();
		if (userAgent.includes('win')) return 'windows';
		return 'macos';
	}

	let platform = $state<'macos' | 'windows'>('macos');
	let downloadUrl = $derived(
		platform === 'windows' ? constants.DOWNLOAD_URL_WINDOWS : constants.DOWNLOAD_URL_MACOS
	);
	let downloadWithSkip = $derived(
		resolve(`/download?skip=1&platform=${platform}` as any)
	);

	interface Props {
		signInHandler?: () => void;
		signUpHandler?: () => void;
		enableApp?: boolean;
		signOut?: () => void;
		isBlog?: boolean;
	}

	let {
		signInHandler = () => {},
		signUpHandler = () => {},
		signOut = () => {},
		isBlog = false,
		enableApp = false
	}: Props = $props();

	let userName = $derived($user?.firstName ? `${$user.firstName} ${$user.lastName}` : $user?.email);
	let searchOpen = $state(false);
	let keyboardShortcut = $state<string | null>(null);

	// Track mount state to avoid hydration issues with user-dependent UI
	let mounted = $state(false);
	onMount(() => {
		mounted = true;
		keyboardShortcut = getKeyboardShortcut();
		platform = detectPlatform();
	});

	async function smoothScrollTo(elementId: string) {
		// Only run in browser environment
		if (!browser) return;

		// Navigate to main page first if not already there
		if (typeof window !== 'undefined' && window.location.pathname !== '/') {
			await goto(resolve('/'));
		}

		// Wait a bit for the page to load, then scroll to element
		setTimeout(async () => {
			if (!browser) return;
			await tick();
			const element = document.getElementById(elementId);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}, 100);
	}

	async function handleSignOut() {
		await signOut();
	}

	function handleSearchClick() {
		searchOpen = true;
	}

	// Keyboard shortcut for search (Cmd+K / Ctrl+K)
	$effect(() => {
		if (!browser) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				searchOpen = true;
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	});
</script>

<header class="fixed top-0 left-0 right-0 z-50 bg-[rgba(22,22,23,.8)] blur-and-saturate">
	<div
		class="self-stretch w-full inline-flex flex-col justify-start items-center gap-2 overflow-hidden px-8"
	>
		<div class="self-stretch h-14 inline-flex items-center gap-2 overflow-hidden justify-between">
			<div class="flex flex-row relative overflow-clip min-w-[calc(var(--sidebar-width)-32px)]">
				<a href={resolve('/')} class="hover:opacity-80 transition-opacity">
					<enhanced:img
						src="../../../../static-shared/img/footerlogo.png"
						alt="logo"
						class="h-7 md:h-10 w-[91px] md:w-[131px]"
						sizes="(max-width: 768px) 91px, 131px"
					/>
					<div
						class="absolute left-[63px] md:left-[88px] top-[50%] -translate-y-1/2 font-normal font-sans md:leading-loose"
					>
						<span
							class="caption px-1.5 border border-solid border-white/60 rounded-md inline-block ml-2 text-white/60 text-[9px] md:text-[10px]"
						>
							{toTitleCase(constants.CHANNEL)}
						</span>
					</div>
				</a>
			</div>

			<!-- Desktop Navigation -->
			<div
				class="hidden md:flex justify-center items-center gap-4 min-[1100px]:gap-8 min-w-0 overflow-hidden mx-auto"
			>
				<a
					href={blogUrl}
					class="hidden min-[1000px]:block justify-start text-white text-[15px] font-normal font-['Instrument_Sans'] leading-loose hover:text-blue-300 transition-colors cursor-pointer whitespace-nowrap"
				>
					Blog
				</a>
				<a
					href="/help"
					class="hidden min-[900px]:block justify-start text-white text-[15px] font-normal font-['Instrument_Sans'] leading-loose hover:text-blue-300 transition-colors cursor-pointer whitespace-nowrap"
				>
					User Guide
				</a>
				<a
					href="/pricing"
					class="justify-start text-white text-[15px] font-normal font-['Instrument_Sans'] leading-loose hover:text-blue-300 transition-colors cursor-pointer whitespace-nowrap"
				>
					Pricing
				</a>

				<button
					onclick={() => smoothScrollTo('agent-section')}
					class="justify-start text-white text-[15px] font-normal font-['Instrument_Sans'] leading-loose hover:text-blue-300 transition-colors cursor-pointer whitespace-nowrap"
				>
					Features
				</button>

				{#if $user?.id && enableApp}
					<!-- Force reload so that COEP/COOP headers are applied -->
					<a
						href={appUrl}
						rel="external"
						target="retainApp"
						class="justify-start text-white text-[15px] font-normal font-['Instrument_Sans'] leading-loose hover:text-blue-300 transition-colors cursor-pointer whitespace-nowrap"
					>
						Web App
					</a>
				{/if}
				<a
					data-sveltekit-reload
					rel="external"
					href={downloadUrl}
					onclick={() => setTimeout(() => goto(downloadWithSkip), 2000)}
					class="group bg-blue-700 py-1.5 px-3 rounded-full text-white text-[15px] font-normal font-['Instrument_Sans'] hover:bg-blue-800 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
				>
					Download
				</a>
			</div>

			<!-- Right side: Search and Sign Up -->
			<div class="hidden md:flex items-center gap-4 min-w-40 flex-shrink-0">
				<button
					onclick={handleSearchClick}
					class="min-w-0 relative flex items-center gap-2 h-8 px-3 rounded-full bg-white/10 hover:bg-white/15 transition-all cursor-text group"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-300"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						stroke-width="2"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
						/>
					</svg>
					<span class="text-sm text-white/60 group-hover:text-white/80">Search...</span>
					{#if keyboardShortcut}
						<kbd
							class="hidden lg:inline-block ml-2 px-1.5 py-0.5 text-xs text-white/40 bg-white/10 rounded"
						>
							{keyboardShortcut}
						</kbd>
					{/if}
				</button>

				{#if !mounted || !$user?.id}
					<!-- Show sign-in/sign-up before mount or when not signed in -->
					<button
						onclick={() => {
							console.log('handleSignIn - header');
							signInHandler();
						}}
						class="justify-start text-white text-[15px] font-normal font-['Instrument_Sans'] leading-loose hover:text-blue-300 transition-colors cursor-pointer whitespace-nowrap"
					>
						Sign In
					</button>
					<button
						onclick={signUpHandler}
						class="px-3 py-1.5 bg-neutral-600 rounded-2xl flex justify-center items-center gap-2 hover:!bg-neutral-500 transition-colors cursor-pointer whitespace-nowrap"
					>
						<div
							class="justify-start text-white text-[15px] font-normal font-['Instrument_Sans'] leading-tight"
						>
							Sign Up
						</div>
					</button>
				{:else}
					<DropdownMenu.Root>
						<DropdownMenu.Trigger>
							<Button
								variant="ghost"
								size="sm"
								class="p-2 h-auto bg-transparent hover:bg-white/10 border-0"
							>
								{#if $user?.picture}
									<img src={$user.picture} alt="Profile" class="w-8 h-8 rounded-full" />
								{:else}
									<div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
										<span class="text-white text-sm font-semibold">
											{$user?.firstName?.[0] || $user?.email?.[0] || 'U'}
										</span>
									</div>
								{/if}
							</Button>
						</DropdownMenu.Trigger>
						<DropdownMenu.Content>
							<DropdownMenu.Group>
								<DropdownMenu.Label>
									{userName}
								</DropdownMenu.Label>
								<DropdownMenu.Item>
									<a href={resolve('/account')} class="w-full">Account</a>
								</DropdownMenu.Item>
								<DropdownMenu.Separator />
								<DropdownMenu.Item onclick={handleSignOut} class="text-red-600">
									Sign Out
								</DropdownMenu.Item>
							</DropdownMenu.Group>
						</DropdownMenu.Content>
					</DropdownMenu.Root>
				{/if}
			</div>

			<!-- Mobile Menu -->
			<MobileNav {signInHandler} {signUpHandler} {signOut} {smoothScrollTo} {platform} />
		</div>
	</div>
</header>

<!-- Search Command Dialog -->
<HelpSearchCommand bind:open={searchOpen} onOpenChange={(open) => (searchOpen = open)} />

<style>
	.blur-and-saturate {
		backdrop-filter: saturate(1.8) blur(20px);
	}
</style>
